import React from 'react';
import { cx } from './cx';

export interface UiInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  /** Icon or prefix inside the left edge; the text is padded so it never overlaps. */
  leading?: React.ReactNode;
  /** Button or suffix inside the right edge. */
  trailing?: React.ReactNode;
  /** sm 36px, md 44px (default), lg 52px. Text stays 16px at every size. */
  inputSize?: 'sm' | 'md' | 'lg';
  containerClassName?: string;
}
export type InputProps = UiInputProps;

export const fieldControlClass = cx(
  'w-full min-w-0 rounded-control border bg-inset font-ui text-body text-primary placeholder:text-tertiary',
  'transition-colors duration-(--k-dur-fast)',
  'focus:outline-none focus:border-focus focus:ring-3 focus:ring-focus/20',
  'disabled:cursor-not-allowed disabled:border-dashed disabled:bg-transparent disabled:text-disabled',
  'read-only:focus:ring-0',
  // Browser autofill paints its own (light) field: keep the inset surface and primary text.
  'autofill:shadow-[inset_0_0_0_1000px_var(--k-surface-inset)] autofill:[-webkit-text-fill-color:var(--k-text-primary)]'
);

/** Text input on the inset surface; 16px text so phones don't zoom on focus. */
const Input = React.forwardRef<HTMLInputElement, UiInputProps>(function Input(
  { invalid, leading, trailing, inputSize = 'md', containerClassName, className, ...rest },
  ref
) {
  return (
    <div className={cx('relative flex min-w-0 items-center', containerClassName)}>
      {leading ? (
        <span className="pointer-events-none absolute left-3 inline-flex items-center text-tertiary [&>svg]:size-4.5" aria-hidden>
          {leading}
        </span>
      ) : null}
      <input
        ref={ref}
        aria-invalid={invalid || rest['aria-invalid'] || undefined}
        className={cx(
          fieldControlClass,
          inputSize === 'lg' ? 'h-13' : inputSize === 'sm' ? 'h-9' : 'h-11',
          leading ? 'pl-10' : 'pl-3',
          trailing ? 'pr-11' : 'pr-3',
          invalid ? 'border-danger' : 'border-edge hover:border-edge-strong',
          className
        )}
        {...rest}
      />
      {trailing ? <span className="absolute right-1.5 inline-flex items-center">{trailing}</span> : null}
    </div>
  );
});

export default Input;
